import type { RealtimeClient } from '@batiflow/client-core';
import { RealtimeRoomSchema } from '@batiflow/contracts';
import { createContext, useContext, useEffect } from 'react';
import { useStore } from 'zustand';
import { useRuntime } from '@/app/providers/runtime-provider';
import type { RoomRegistry, RoomScope } from './rooms';

export interface RealtimeContextValue {
  /** The connection of the signed-in session; null when realtime is off or the session not ready. */
  client: RealtimeClient | null;
  registry: RoomRegistry;
}

export const RealtimeContext = createContext<RealtimeContextValue | null>(null);

/** The realtime client, or null (not signed in, switched off, or outside the provider). */
export function useRealtime(): RealtimeClient | null {
  return useContext(RealtimeContext)?.client ?? null;
}

/** Whether the realtime connection is open now (the UI stops its slow polling while it is). */
export function useRealtimeConnected(): boolean {
  const { realtimeStore } = useRuntime();
  return useStore(realtimeStore, (state) => state.link === 'up');
}

/**
 * Subscribes the room for as long as the calling component is mounted (route-based subscription:
 * the project screen takes `space:{id}`, the document viewer `doc:{id}`). `null` subscribes
 * nothing, so a screen can call it before the id it needs is known. The `scope` tells which
 * cached queries belong to the room (refetched after a reconnection, removed on a revocation).
 */
export function useRealtimeRoom(room: string | null, scope: RoomScope = {}): void {
  const context = useContext(RealtimeContext);
  const client = context?.client ?? null;
  const registry = context?.registry ?? null;
  const scopeKey = JSON.stringify({ spaceId: scope.spaceId, fragments: scope.fragments });
  useEffect(() => {
    // A room name the contract refuses (a malformed id in the address) is simply not subscribed.
    if (!room || !registry || !RealtimeRoomSchema.safeParse(room).success) return;
    const remove = registry.add(room, JSON.parse(scopeKey) as RoomScope);
    client?.subscribe(room);
    return () => {
      remove();
      client?.unsubscribe(room);
    };
  }, [room, client, registry, scopeKey]);
}

/** The most rooms one screen takes in one go: the server allows 200 per connection. */
const MAX_ROOMS_PER_SCREEN = 100;

/**
 * `useRealtimeRoom` for a list of rooms (the chat list pane takes the room of every channel it
 * shows, so unread badges follow messages in channels that are not open). Rooms the contract
 * refuses are skipped, and at most `MAX_ROOMS_PER_SCREEN` are taken.
 */
export function useRealtimeRooms(rooms: readonly string[], scope: RoomScope = {}): void {
  const context = useContext(RealtimeContext);
  const client = context?.client ?? null;
  const registry = context?.registry ?? null;
  const key = [...new Set(rooms)].sort().join('|');
  const scopeKey = JSON.stringify({ spaceId: scope.spaceId, fragments: scope.fragments });
  useEffect(() => {
    if (!registry || key === '') return;
    const wanted = key
      .split('|')
      .filter((room) => RealtimeRoomSchema.safeParse(room).success)
      .slice(0, MAX_ROOMS_PER_SCREEN);
    const removers = wanted.map((room) => registry.add(room, JSON.parse(scopeKey) as RoomScope));
    for (const room of wanted) client?.subscribe(room);
    return () => {
      for (const remove of removers) remove();
      for (const room of wanted) client?.unsubscribe(room);
    };
  }, [key, scopeKey, client, registry]);
}
